import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Users, 
  Gift, 
  Shield,
  BarChart3,
  ShoppingCart,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Wallet,
  Package
} from 'lucide-react';
import { api, UserProfile, AdminGiftGroup } from '../lib/api';
import { toast } from 'sonner';
import { AmazonPurchasesPanel } from './AmazonPurchasesPanel';

interface AdminDashboardProps {
  onNavigate: (screen: string) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState({
    totalGiftGroups: 0,
    totalUsers: 0,
    totalEscrow: 0,
    purchasesCount: 0,
    purchasesAmount: 0,
  });
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPagination, setUsersPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Gift Groups state
  const [giftGroups, setGiftGroups] = useState<AdminGiftGroup[]>([]);
  const [giftGroupsLoading, setGiftGroupsLoading] = useState(false);
  const [giftGroupsPagination, setGiftGroupsPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
  });
  const [giftGroupsSearchTerm, setGiftGroupsSearchTerm] = useState('');

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      
      // Load user profile to get admin level
      const profile = await api.getProfile();
      setUserProfile(profile);
      
      // Check if user is actually an admin
      if (!profile.adminLevel) {
        toast.error('Access Denied', {
          description: 'You do not have admin privileges',
        });
        onNavigate('dashboard');
        return;
      }
      
      // Fetch admin stats from API
      try {
        const statsData = await api.getAdminStats();
        setStats(statsData);
      } catch (statsError) {
        console.error('Error loading admin stats:', statsError);
        // Keep default stats on error
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin dashboard');
      onNavigate('dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (offset: number = 0, search: string = '') => {
    try {
      setUsersLoading(true);
      const response = await api.getAdminUsers({
        limit: usersPagination.limit,
        offset,
        search: search || undefined,
      });
      setUsers(response.users);
      setUsersPagination(response.pagination);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadGiftGroups = async (offset: number = 0, search: string = '') => {
    try {
      setGiftGroupsLoading(true);
      const response = await api.getAdminGiftGroups({
        limit: giftGroupsPagination.limit,
        offset,
        search: search || undefined,
      });
      setGiftGroups(response.giftGroups);
      setGiftGroupsPagination(response.pagination);
    } catch (error) {
      console.error('Error loading gift groups:', error);
      toast.error('Failed to load gift groups');
    } finally {
      setGiftGroupsLoading(false);
    }
  };

  const handleSearchUsers = () => {
    loadUsers(0, searchTerm);
  };

  const handleSearchGiftGroups = () => {
    loadGiftGroups(0, giftGroupsSearchTerm);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, usersPagination.offset - usersPagination.limit);
    loadUsers(newOffset, searchTerm);
  };

  const handleNextPage = () => {
    const newOffset = usersPagination.offset + usersPagination.limit;
    if (newOffset < usersPagination.total) {
      loadUsers(newOffset, searchTerm);
    }
  };

  const handleGiftGroupsPrevPage = () => {
    const newOffset = Math.max(0, giftGroupsPagination.offset - giftGroupsPagination.limit);
    loadGiftGroups(newOffset, giftGroupsSearchTerm);
  };

  const handleGiftGroupsNextPage = () => {
    const newOffset = giftGroupsPagination.offset + giftGroupsPagination.limit;
    if (newOffset < giftGroupsPagination.total) {
      loadGiftGroups(newOffset, giftGroupsSearchTerm);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const getAdminBadge = (adminLevel: number | undefined) => {
    if (!adminLevel) return null;
    switch (adminLevel) {
      case 1:
        return <Badge className="bg-red-600 text-white">Super Admin</Badge>;
      case 2:
        return <Badge className="bg-orange-600 text-white">Admin</Badge>;
      case 3:
        return <Badge className="bg-yellow-600 text-white">Support</Badge>;
      default:
        return null;
    }
  };

  const getAdminLevelBadge = () => {
    if (!userProfile?.adminLevel) return null;
    
    switch (userProfile.adminLevel) {
      case 1:
        return <Badge className="bg-red-600">Super Admin</Badge>;
      case 2:
        return <Badge className="bg-orange-600">Admin</Badge>;
      case 3:
        return <Badge className="bg-yellow-600">Support</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getGiftGroupStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_acceptance':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'suggesting':
        return <Badge className="bg-blue-100 text-blue-800">Suggesting</Badge>;
      case 'polling':
        return <Badge className="bg-purple-100 text-purple-800">Polling</Badge>;
      case 'ordering':
        return <Badge className="bg-orange-100 text-orange-800">Ordering</Badge>;
      case 'ordered':
        return <Badge className="bg-indigo-100 text-indigo-800">Ordered</Badge>;
      case 'delivered':
        return <Badge className="bg-emerald-100 text-emerald-800">Delivered</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-primary" />
              <h1 className="text-emerald-700">System Administration</h1>
            </div>
            <div className="flex items-center gap-2">
              {getAdminLevelBadge()}
              {userProfile?.adminLevel && (
                <span className="text-sm text-muted-foreground">
                  Access Level {userProfile.adminLevel}
                </span>
              )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Gift className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gift Groups</p>
                  <p className="text-2xl">{stats.totalGiftGroups}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Users</p>
                  <p className="text-2xl">{stats.totalUsers}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Wallet className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Escrow</p>
                  <p className="text-2xl">${stats.totalEscrow.toFixed(2)}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Purchases</p>
                  <p className="text-2xl">{stats.purchasesCount}</p>
                  <p className="text-sm text-muted-foreground">${stats.purchasesAmount.toFixed(2)} total</p>
                </div>
              </div>
            </Card>
          </div>

        {/* Admin Tabs */}
        <Tabs defaultValue="overview" className="w-full" onValueChange={(value) => {
          if (value === 'users' && users.length === 0) {
            loadUsers();
          }
          if (value === 'groups' && giftGroups.length === 0) {
            loadGiftGroups();
          }
        }}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="groups">
              <Gift className="w-4 h-4 mr-2" />
              Gift Groups
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="purchases">
              <Package className="w-4 h-4 mr-2" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="contributions">
              <Wallet className="w-4 h-4 mr-2" />
              Contributions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Platform Overview</h2>
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Admin analytics and reporting coming in Phase 3
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="groups" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Gift Group Management</h2>
              
              {/* Search Bar */}
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by title or creator..."
                    value={giftGroupsSearchTerm}
                    onChange={(e) => setGiftGroupsSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchGiftGroups()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearchGiftGroups} variant="outline">
                  Search
                </Button>
              </div>

              {/* Gift Groups Table */}
              {giftGroupsLoading ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Loading gift groups...</p>
                </div>
              ) : giftGroups.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {giftGroupsSearchTerm ? 'No gift groups found matching your search' : 'No gift groups in the system yet'}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Creator</TableHead>
                        <TableHead>Gift Date</TableHead>
                        <TableHead>Members</TableHead>
                        <TableHead>Pool</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {giftGroups.map((group) => (
                        <TableRow key={group.id}>
                          <TableCell className="font-medium">
                            {group.title}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">{group.typeName}</span>
                          </TableCell>
                          <TableCell>
                            {group.creatorName}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-muted-foreground" />
                              {formatDate(group.giftDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col text-sm">
                              <span>{group.gifterCount} gifter{group.gifterCount !== 1 ? 's' : ''}</span>
                              <span className="text-muted-foreground">{group.gifteeCount} giftee{group.gifteeCount !== 1 ? 's' : ''}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            ${group.totalPool.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {getGiftGroupStatusBadge(group.status)}
                          </TableCell>
                          <TableCell>
                            {formatDate(group.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {giftGroupsPagination.offset + 1} - {Math.min(giftGroupsPagination.offset + giftGroups.length, giftGroupsPagination.total)} of {giftGroupsPagination.total} gift groups
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGiftGroupsPrevPage}
                        disabled={giftGroupsPagination.offset === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGiftGroupsNextPage}
                        disabled={giftGroupsPagination.offset + giftGroupsPagination.limit >= giftGroupsPagination.total}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">User Management</h2>
              
              {/* Search Bar */}
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email, name, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearchUsers} variant="outline">
                  Search
                </Button>
              </div>

              {/* Users Table */}
              {usersLoading ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm ? 'No users found matching your search' : 'No users in the system yet'}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email/Phone</TableHead>
                        <TableHead>Credit Balance</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Last Login</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {user.name || '-'}
                          </TableCell>
                          <TableCell>
                            {user.email || user.phone || '-'}
                          </TableCell>
                          <TableCell>
                            ${(user.creditBalance || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {getAdminBadge(user.adminLevel) || <span className="text-muted-foreground">User</span>}
                          </TableCell>
                          <TableCell>
                            {formatDate(user.createdAt)}
                          </TableCell>
                          <TableCell>
                            {formatDate(user.lastLogin)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {usersPagination.offset + 1} - {Math.min(usersPagination.offset + users.length, usersPagination.total)} of {usersPagination.total} users
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={usersPagination.offset === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={usersPagination.offset + usersPagination.limit >= usersPagination.total}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="purchases" className="mt-6">
            <AmazonPurchasesPanel onNavigate={onNavigate} />
          </TabsContent>

          <TabsContent value="contributions" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Contributions Management</h2>
              <div className="text-center py-12">
                <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Contribution tracking and management coming in Phase 3
                </p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}